<template>
  <div
    :class="['RendererMermaid', { 'RendererMermaid--resizing': isResizing }]"
  >
    <Teleport :to="previewToolbarPortalEl" :disabled="!previewToolbarPortalEl">
      <MermaidPreviewControls
        class="RendererMermaid__toolbar"
        :zoomPercent="mermaidZoomPercent"
        :canZoomIn="mermaidZoomPercent < 500"
        :canZoomOut="mermaidZoomPercent > 25"
        :viewportLocked="mermaidViewportLocked"
        :downloadOptions="downloadOptions"
        @zoomIn="zoomIn"
        @zoomOut="zoomOut"
        @resetZoom="resetZoom"
        @zoomTo100="zoomTo100"
        @fitView="fitView"
        @toggleViewportLock="mermaidViewportLocked = !mermaidViewportLocked"
      />
    </Teleport>
    <div v-if="loading" class="RendererMermaid__loading">
      <span class="RendererMermaid__spinner" />
      <span class="RendererMermaid__loading-text">Rendering diagram…</span>
    </div>
    <div v-else-if="error !== null" class="RendererMermaid__error">
      <span class="RendererMermaid__error-icon">⚠</span>
      <span v-if="error" class="RendererMermaid__error-message">{{
        error
      }}</span>
      <UIButton
        class="RendererMermaid__fix-button"
        size="md"
        theme="primary"
        :isDisabled="!canQueueMermaidFix"
        :isLoading="previewStore.mermaidFixAwaitingResponse"
        @click="requestMermaidFix"
      >
        Fix chart
      </UIButton>
      <details class="RendererMermaid__source-details">
        <summary>View source</summary>
        <pre class="RendererMermaid__source">{{ content }}</pre>
      </details>
    </div>
    <template v-else-if="renderedSvg">
      <div
        ref="outputRef"
        class="RendererMermaid__output"
        :class="{ 'RendererMermaid__output--locked': mermaidViewportLocked }"
      >
        <div
          class="RendererMermaid__zoom-wrapper"
          :style="{ transform: `scale(${zoomLevel})` }"
        >
          <div class="RendererMermaid__svg-container" v-html="renderedSvg" />
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import type { Ref } from 'vue'
import { computed, inject, nextTick, ref, watch } from 'vue'

import mermaid from 'mermaid'

import { usePreviewStore } from '@/store/preview'
import { MERMAID_AUTO_FIX_MAX } from '@/store/preview/constants'
import UIButton from '@/views/components/ui/buttons/UIButton.vue'
import type { IUIDropdownOption } from '@/views/components/ui/dropdowns/UIDropdown.vue'

import MermaidPreviewControls from '../MermaidPreviewControls.vue'
import { previewToolbarPortalKey } from '../previewToolbarPortalKey'

const previewStore = usePreviewStore()

const props = defineProps<{
  content: string
  isResizing: boolean
}>()

const previewToolbarPortalEl = inject(
  previewToolbarPortalKey,
  ref(null),
) as Ref<HTMLElement | null>

const loading = ref(false)
const error = ref<string | null>(null)
const renderedSvg = ref<string | null>(null)
const outputRef = ref<HTMLElement>()
/** Fallback if layout measurement fails */
const FALLBACK_ZOOM = 1
const ZOOM_STEP = 0.25
const MIN_ZOOM = 0.25
const MAX_ZOOM = 5
/** Padding inside the preview viewport when fitting (0.92 = ~8% margin). */
const FIT_VIEWPORT_PADDING = 0.92

const zoomLevel = ref(FALLBACK_ZOOM)
/** Last “fit to preview” zoom for this diagram — used by Reset. */
const baselineFitZoom = ref(FALLBACK_ZOOM)

const mermaidViewportLocked = ref(false)
const mermaidZoomPercent = computed(() => Math.round(zoomLevel.value * 100))

let renderGeneration = 0

function snapZoom(scale: number): number {
  const snapped = Math.round(scale / ZOOM_STEP) * ZOOM_STEP
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, snapped))
}

/**
 * After SVG is in the DOM, pick a default zoom so the diagram fits the preview area.
 * Measures layout at zoom 1, then scales to fit width/height with padding.
 */
async function applyFitZoomFromLayout() {
  const gen = renderGeneration
  await nextTick()
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
  })
  if (gen !== renderGeneration) return

  const output = outputRef.value
  if (!output) return
  const wrapper = output.querySelector(
    '.RendererMermaid__zoom-wrapper',
  ) as HTMLElement | null
  if (!wrapper) return

  zoomLevel.value = 1
  await nextTick()
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
  if (gen !== renderGeneration) return

  const vw = output.clientWidth
  const vh = output.clientHeight
  const cw = Math.max(wrapper.scrollWidth, wrapper.offsetWidth)
  const ch = Math.max(wrapper.scrollHeight, wrapper.offsetHeight)

  if (vw < 8 || vh < 8 || cw < 1 || ch < 1) {
    const z = snapZoom(FALLBACK_ZOOM)
    baselineFitZoom.value = z
    zoomLevel.value = z
    return
  }

  const fitW = (vw * FIT_VIEWPORT_PADDING) / cw
  const fitH = (vh * FIT_VIEWPORT_PADDING) / ch
  const fit = Math.min(fitW, fitH)
  const z = snapZoom(fit)
  baselineFitZoom.value = z
  zoomLevel.value = z
}

const canQueueMermaidFix = computed(
  () => previewStore.mermaidAutoFixSentCount < MERMAID_AUTO_FIX_MAX,
)

function requestMermaidFix() {
  if (!canQueueMermaidFix.value || error.value === null) return
  previewStore.queueMermaidFixRequest(error.value, props.content)
}

/** ViewBox of Mermaid’s built-in error canvas (see mermaid `draw` for syntax error). */
const MERMAID_BUILTIN_ERROR_VIEWBOX = '0 0 2412 512'
/** First line of text drawn on that canvas — must match a real `<text class="error-text">`, not a substring of the raw SVG (theme CSS contains `.error-text` / `.error-icon` on every diagram). */
const MERMAID_BUILTIN_ERROR_LABEL = 'Syntax error in text'

function normalizeSvgViewBox(vb: string | null): string | null {
  if (!vb) return null
  return vb.replace(/\s+/g, ' ').trim()
}

/** True only for Mermaid’s internal “syntax error” SVG, not valid diagrams (which also embed `.error-text` rules in `<style>`). */
function isMermaidBuiltinErrorSvg(svg: string): boolean {
  try {
    const doc = new DOMParser().parseFromString(svg, 'image/svg+xml')
    if (doc.querySelector('parsererror')) return false
    const svgEl = doc.querySelector('svg')
    if (!svgEl) return false
    if (
      normalizeSvgViewBox(svgEl.getAttribute('viewBox')) !==
      MERMAID_BUILTIN_ERROR_VIEWBOX
    ) {
      return false
    }
    for (const t of Array.from(svgEl.querySelectorAll('text'))) {
      const classes =
        t.getAttribute('class')?.split(/\s+/).filter(Boolean) ?? []
      if (!classes.includes('error-text')) continue
      if (t.textContent?.trim() === MERMAID_BUILTIN_ERROR_LABEL) return true
    }
    return false
  } catch {
    return false
  }
}

function mermaidErrorMessage(err: unknown): string {
  if (typeof err === 'string') return err
  if (err instanceof Error) return err.message
  if (
    err !== null &&
    typeof err === 'object' &&
    'str' in err &&
    typeof (err as { str: unknown }).str === 'string'
  ) {
    return (err as { str: string }).str
  }
  if (
    err !== null &&
    typeof err === 'object' &&
    'message' in err &&
    typeof (err as { message: unknown }).message === 'string'
  ) {
    return (err as { message: string }).message
  }
  return String(err)
}

/** Text Mermaid put into the built-in error SVG (same strings as their error renderer, not app copy). */
function mermaidErrorTextFromBuiltinSvg(svg: string): string {
  try {
    const doc = new DOMParser().parseFromString(svg, 'image/svg+xml')
    const nodes = doc.querySelectorAll('text.error-text')
    const parts: string[] = []
    nodes.forEach((el) => {
      const t = el.textContent?.trim()
      if (t) parts.push(t)
    })
    const withoutVersion = parts.filter((p) => !/^mermaid version\b/i.test(p))
    const joined = (withoutVersion.length ? withoutVersion : parts).join(' · ')
    if (joined.trim()) return joined.trim()
  } catch {
    /* ignore */
  }
  return ''
}

let initialized = false

function ensureMermaidInit() {
  if (initialized) return
  mermaid.initialize({
    startOnLoad: false,
    securityLevel: 'loose',
  })
  initialized = true
}

async function render() {
  if (!props.content.trim()) {
    loading.value = false
    error.value = null
    renderedSvg.value = null
    return
  }

  renderGeneration += 1
  const gen = renderGeneration

  loading.value = true
  error.value = null
  renderedSvg.value = null

  const previousParseError = mermaid.parseError
  let parseErrorDetail: string | undefined
  mermaid.parseError = (err: unknown, hash?: unknown) => {
    parseErrorDetail = typeof err === 'string' ? err : mermaidErrorMessage(err)
    previousParseError?.(err as never, hash as never)
  }

  try {
    ensureMermaidInit()
    await mermaid.parse(props.content)
    if (gen !== renderGeneration) return

    const id = `mermaid-preview-${Date.now()}`
    const { svg } = await mermaid.render(id, props.content)
    if (gen !== renderGeneration) return
    if (isMermaidBuiltinErrorSvg(svg)) {
      const msg = [
        parseErrorDetail?.trim(),
        mermaidErrorTextFromBuiltinSvg(svg).trim(),
      ].find(Boolean)
      error.value = msg ?? ''
      renderedSvg.value = null
      return
    }
    renderedSvg.value = svg
    zoomLevel.value = 1
    previewStore.resetMermaidAutoFixCount()
  } catch (err) {
    const msg = mermaidErrorMessage(err).trim()
    error.value = msg || ''
  } finally {
    mermaid.parseError = previousParseError
    loading.value = false
  }

  if (gen === renderGeneration && renderedSvg.value) {
    await applyFitZoomFromLayout()
  }
}

function zoomIn() {
  zoomLevel.value = Math.min(MAX_ZOOM, zoomLevel.value + ZOOM_STEP)
}

function zoomOut() {
  zoomLevel.value = Math.max(MIN_ZOOM, zoomLevel.value - ZOOM_STEP)
}

function resetZoom() {
  zoomLevel.value = baselineFitZoom.value
}

function zoomTo100() {
  zoomLevel.value = snapZoom(1)
}

async function fitView() {
  await applyFitZoomFromLayout()
}

function getSvgExportDimensions(svg: SVGSVGElement): {
  width: number
  height: number
} {
  const vb = svg.viewBox?.baseVal
  if (vb && vb.width > 0 && vb.height > 0) {
    return { width: vb.width, height: vb.height }
  }
  const w = parseFloat(
    String(svg.getAttribute('width') || '').replace(/px$/i, ''),
  )
  const h = parseFloat(
    String(svg.getAttribute('height') || '').replace(/px$/i, ''),
  )
  if (w > 0 && h > 0) {
    return { width: w, height: h }
  }
  return { width: svg.clientWidth || 800, height: svg.clientHeight || 600 }
}

const SVG_NS = 'http://www.w3.org/2000/svg'

/** foreignObject clips, and mermaid sizes each label box to the exact text width it
 * measured here — a few pixels of font difference elsewhere cut the last characters off. */
const EXPORT_LABEL_OVERFLOW_CSS = 'foreignObject{overflow:visible;}'

/** Text metrics the HTML labels inherit from the page. Nothing in the exported file
 * carries them, so `line-height: 1.5` (24px boxes) becomes `normal` (19px) and the
 * text rides high in the box mermaid sized for it. */
const EXPORT_INHERITED_LABEL_PROPS = [
  'line-height',
  'letter-spacing',
  'word-spacing',
  'text-rendering',
]

function labelTypographyCss(svg: SVGSVGElement): string {
  const label = svg.querySelector('foreignObject div')
  if (!label) return ''
  const computed = getComputedStyle(label)
  const declarations = EXPORT_INHERITED_LABEL_PROPS.map((prop) => {
    const value = computed.getPropertyValue(prop).trim()
    return value ? `${prop}:${value};` : ''
  }).join('')
  return declarations ? `foreignObject div{${declarations}}` : ''
}

/**
 * Clone for export: explicit pixel size + xmlns so rasterization and standalone
 * viewers work, plus the styling the page was contributing.
 *
 * Mermaid sizes the live SVG for the preview pane (`width="100%"` + `max-width`),
 * which lets viewers stretch a downloaded file; the viewBox is its natural size.
 */
function prepareSvgCloneForExport(svg: SVGSVGElement): SVGSVGElement {
  const clone = svg.cloneNode(true) as SVGSVGElement
  const { width, height } = getSvgExportDimensions(svg)
  const w = Math.max(1, Math.round(width))
  const h = Math.max(1, Math.round(height))
  if (!clone.getAttribute('xmlns')) {
    clone.setAttribute('xmlns', SVG_NS)
  }
  clone.setAttribute('width', String(w))
  clone.setAttribute('height', String(h))
  clone.removeAttribute('style')

  const style = document.createElementNS(SVG_NS, 'style')
  style.textContent = `${EXPORT_LABEL_OVERFLOW_CSS}${labelTypographyCss(svg)}`
  clone.appendChild(style)

  return clone
}

/** Revoking straight after `click()` races the download. */
const OBJECT_URL_REVOKE_DELAY_MS = 1000

function triggerDownload(href: string, filename: string) {
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  a.rel = 'noopener'
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

function downloadSvg() {
  const container = outputRef.value
  if (!container) return
  const svgEl = container.querySelector('svg')
  if (!svgEl) return

  const svgClone = prepareSvgCloneForExport(svgEl as SVGSVGElement)
  const svgString = `<?xml version="1.0" encoding="UTF-8"?>\n${new XMLSerializer().serializeToString(
    svgClone,
  )}`
  const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  triggerDownload(url, `mermaid-diagram-${Date.now()}.svg`)
  setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_REVOKE_DELAY_MS)
}

const PNG_EXPORT_SCALE = 2

function downloadPng() {
  const container = outputRef.value
  if (!container) return
  const svgEl = container.querySelector('svg')
  if (!svgEl) return

  const clone = prepareSvgCloneForExport(svgEl as SVGSVGElement)
  const svgString = new XMLSerializer().serializeToString(clone)
  const { width: w, height: h } = getSvgExportDimensions(svgEl as SVGSVGElement)
  const nw = Math.max(1, Math.round(w))
  const nh = Math.max(1, Math.round(h))

  // Use a data: URL for the SVG — CSP img-src allows data: but not blob: (see security-headers.ts).
  const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    svgString,
  )}`

  const img = new Image()
  const filename = `mermaid-diagram-${Date.now()}.png`

  img.onload = () => {
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.ceil(nw * PNG_EXPORT_SCALE))
    canvas.height = Math.max(1, Math.ceil(nh * PNG_EXPORT_SCALE))
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.scale(PNG_EXPORT_SCALE, PNG_EXPORT_SCALE)
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, nw, nh)
    ctx.drawImage(img, 0, 0, nw, nh)

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const pngUrl = URL.createObjectURL(blob)
          triggerDownload(pngUrl, filename)
          setTimeout(
            () => URL.revokeObjectURL(pngUrl),
            OBJECT_URL_REVOKE_DELAY_MS,
          )
        } else {
          triggerDownload(canvas.toDataURL('image/png'), filename)
        }
      },
      'image/png',
      1,
    )
  }
  img.onerror = () => {
    // Malformed SVG or oversized data URL; nothing to download.
  }
  img.src = svgDataUrl
}

const downloadOptions = computed<IUIDropdownOption[]>(() => [
  {
    label: 'Download SVG',
    value: 'svg',
    onClick: () => downloadSvg(),
  },
  {
    label: 'Download PNG',
    value: 'png',
    onClick: () => downloadPng(),
  },
])

watch(
  () => props.content,
  () => {
    void render()
  },
  { immediate: true },
)

defineExpose({
  zoomLevel,
  zoomIn,
  zoomOut,
  resetZoom,
  zoomTo100,
  fitView,
  downloadSvg,
  downloadPng,
})
</script>

<style lang="scss" scoped>
.RendererMermaid {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  min-height: 0;
  @include customScrollbarInset(1rem);

  &.RendererMermaid--resizing {
    pointer-events: none;
    user-select: none;
  }
}

.RendererMermaid__toolbar {
  flex-shrink: 0;
  align-self: stretch;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.5rem;
}

.RendererMermaid__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 0.75rem;
  color: $color-neutral-100;
}

.RendererMermaid__spinner {
  width: 1.5rem;
  height: 1.5rem;
  border: 2px solid $color-neutral-40;
  border-top-color: $color-primary-40;
  border-radius: 50%;
  animation: RendererMermaid-spin 0.8s linear infinite;
}

@keyframes RendererMermaid-spin {
  to {
    transform: rotate(360deg);
  }
}

.RendererMermaid__loading-text {
  font-size: 0.875rem;
}

.RendererMermaid__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 0.5rem;
  padding: 1rem;
  max-width: 100%;
  color: $color-error-50;
}

.RendererMermaid__error-icon {
  font-size: 1.5rem;
}

.RendererMermaid__error-message {
  font-size: 0.875rem;
  text-align: center;
}

.RendererMermaid__fix-button {
  margin-top: 0.75rem;
}

.RendererMermaid__source-details {
  margin-top: 0.5rem;
  width: 100%;
  max-width: 100%;

  summary {
    cursor: pointer;
    font-size: 0.75rem;
    color: $color-neutral-100;
  }
}

.RendererMermaid__source {
  margin-top: 0.5rem;
  @include customScrollbarInset(0.75rem);
  background: $color-neutral-10;
  border-radius: var(--radius-xs);
  font-size: 0.75rem;
  max-height: 12rem;
  white-space: pre-wrap;
  word-break: break-all;
}

.RendererMermaid__output {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  align-self: stretch;
  @include customScrollbar;
  min-height: 0;

  &.RendererMermaid__output--locked {
    overflow: hidden;
    pointer-events: none;
    user-select: none;
  }
}

.RendererMermaid__zoom-wrapper {
  transform-origin: center center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.RendererMermaid__svg-container {
  display: flex;
  align-items: center;
  justify-content: center;

  :deep(svg) {
    max-width: 100%;
    height: auto;
  }
}
</style>
